import "bootstrap/dist/css/bootstrap.min.css";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import {
  DropdownIndicator,
  TransactionComboboxClearIndicator,
  TransactionComboboxControl,
} from "components/Transaction/TransactionColumn";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  useUpdateTaskAssigneeAdminMutation,
  useUpdateTaskAssigneeProviderMutation,
} from "store/apis/serviceTeam";
import { useSetOnHoldMutation } from "store/apis/task";
import { GlobalStatusType, Task1120Tracker } from "types/Models/TaskTracker";
import { BackendError } from "types/utils/error";
import { TaskStateChangeModal } from "components/Task/TaskStateChangeModal";
import {
  STATUS_DRAFT_SHARED,
  FILTER_BOOKS_PENDING,
  STATUS_ON_HOLD,
  STATUS_READY_TO_FILE,
  STATUS_FILED,
  STATUS_INFORMATION_REQUIRED,
  STATUS_MANDATORY_INFORMATION_MISSING,
  STATUS_INTERNAL_REVIEW,
  STATUS_MI_RAISED_WITH_BOOKS,
  STATUS_IN_FINAL_REVIEW,
  STATUS_PREPARATION_IN_PROGRESS,
  STATUS_READY_TO_PICK,
  STATUS_UNPAID,
} from "../Task1120FilterOptions";

export const globalStatusCustomOptions: Record<
  GlobalStatusType,
  Record<string, string>
> = {
  [STATUS_UNPAID]: {
    color: "orange",
    text: "Unpaid",
  },
  [FILTER_BOOKS_PENDING]: {
    color: "orange",
    text: "Books Pending",
  },
  [STATUS_MANDATORY_INFORMATION_MISSING]: {
    color: "red",
    text: "Mandatory information missing",
  },
  [STATUS_READY_TO_PICK]: {
    color: "green",
    text: "Ready to be picked",
  },
  [STATUS_PREPARATION_IN_PROGRESS]: {
    color: "yellow",
    text: "Preparation in progress",
  },
  [STATUS_INFORMATION_REQUIRED]: {
    color: "red",
    text: "MI raised with client",
  },
  [STATUS_MI_RAISED_WITH_BOOKS]: {
    color: "red",
    text: "MI raised with books",
  },
  [STATUS_INTERNAL_REVIEW]: {
    color: "yellow",
    text: "In review",
  },
  [STATUS_IN_FINAL_REVIEW]: {
    color: "yellow",
    text: "In final review",
  },
  [STATUS_ON_HOLD]: {
    color: "yellow",
    text: "On hold",
  },
  [STATUS_DRAFT_SHARED]: {
    color: "yellow",
    text: "Draft shared with client",
  },
  [STATUS_READY_TO_FILE]: {
    color: "green",
    text: "Ready to file",
  },
  [STATUS_FILED]: {
    color: "green",
    text: "E-filed",
  },
};

const MANUAL_STATUSES: GlobalStatusType[] = [
  STATUS_READY_TO_PICK,
  STATUS_PREPARATION_IN_PROGRESS,
  STATUS_INFORMATION_REQUIRED,
  STATUS_MI_RAISED_WITH_BOOKS,
  STATUS_INTERNAL_REVIEW,
  STATUS_IN_FINAL_REVIEW,
  STATUS_ON_HOLD,
  STATUS_DRAFT_SHARED,
  STATUS_READY_TO_FILE,
  STATUS_FILED,
];

const globalStatusOptions = MANUAL_STATUSES.map((value) => ({
  label: (
    <Tag
      icon={false}
      tagType={globalStatusCustomOptions[value]?.color as TagColorType}
      rounded
    >
      {globalStatusCustomOptions[value]?.text}
    </Tag>
  ),
  value,
}));

export const TaskGlobalStatus = ({
  taskData,
}: {
  taskData: Task1120Tracker;
}) => {
  const { alertToast, successToast } = useToast();
  const { isCpa, isAdmin } = useRoleBasedView();
  const globalStatus = taskData?.task_1120_tracker_global_status;
  const [pendingOnHold, setPendingOnHold] = useState(false);

  const [updateTaskAssigneeForAdmin, { isLoading: isUpdatingTaskAdmin }] =
    useUpdateTaskAssigneeAdminMutation();

  const [updateTaskAssigneeForProvider, { isLoading: isUpdatingTaskProvider }] =
    useUpdateTaskAssigneeProviderMutation();

  const [setOnHold, { isLoading: isSettingOnHold }] = useSetOnHoldMutation();

  let updateTaskAssignee = isAdmin
    ? updateTaskAssigneeForAdmin
    : updateTaskAssigneeForProvider;

  const onGlobalStatusChange = async (value?: string | null) => {
    if (value === STATUS_ON_HOLD) {
      setPendingOnHold(true);
      return;
    }
    try {
      await updateTaskAssignee({
        taskId: taskData?.uuid,
        form_1120_global_status: value,
      }).unwrap();
      successToast({ message: "Global status updated successfully!" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleOnHoldConfirm = async (description?: string) => {
    try {
      await setOnHold({
        taskId: taskData?.uuid,
        description: description ?? "",
      }).unwrap();
      successToast({ message: "Global status updated successfully!" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      setPendingOnHold(false);
    }
  };

  const isFiled = globalStatus === STATUS_FILED;
  const disabled = !MANUAL_STATUSES.includes(globalStatus as GlobalStatusType);

  const selectedOption = globalStatus
    ? [
        {
          label: (
            <Tag
              icon={false}
              tagType={
                globalStatusCustomOptions[globalStatus]?.color as TagColorType
              }
              rounded
            >
              {globalStatusCustomOptions[globalStatus]?.text}
            </Tag>
          ),
          value: globalStatus as string,
        },
      ]
    : [];

  let options = [...selectedOption, ...globalStatusOptions];

  if (
    globalStatus &&
    MANUAL_STATUSES.includes(globalStatus as GlobalStatusType)
  ) {
    options = [
      ...selectedOption,
      ...globalStatusOptions.filter((option) => option.value !== globalStatus),
    ];
  }

  return (
    <div className="t-group t-select-none" onClick={(e) => e.stopPropagation()}>
      {pendingOnHold && (
        <TaskStateChangeModal
          isOpen
          onClose={() => setPendingOnHold(false)}
          onConfirm={handleOnHoldConfirm}
          targetLabel="On Hold"
          isOnHold
          isLoading={isSettingOnHold}
        />
      )}
      <Combobox
        size="small"
        menuPlacement="auto"
        creatable={false}
        isLoading={isUpdatingTaskAdmin || isUpdatingTaskProvider}
        isDisabled={isFiled}
        onChange={(prop: SingleValue<OptionData> | MultiValue<OptionData>) =>
          onGlobalStatusChange((prop as SingleValue<OptionData>)?.value)
        }
        components={{
          Control: TransactionComboboxControl,
          DropdownIndicator: disabled ? () => null : DropdownIndicator,
          ClearIndicator: () => null,
        }}
        options={options}
        value={
          globalStatus
            ? {
                label: (
                  <Tag
                    icon={false}
                    tagType={
                      globalStatusCustomOptions[globalStatus]
                        ?.color as TagColorType
                    }
                    rounded
                  >
                    {globalStatusCustomOptions[globalStatus]?.text}
                  </Tag>
                ),
                value: globalStatus as string,
              }
            : null
        }
        menuPortalTarget={document.body}
        styles={{
          menu: (base) => ({
            ...base,
            width: 260,
          }),
        }}
      />
    </div>
  );
};
